import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {MatOption} from '@angular/material/select';
import {ClaimMetadataRequest} from '../../entity/metadata';
import {AuthService} from '../../../authentication/auth-service';
import {ActivatedRoute} from '@angular/router';
import {ClaimCreatorService} from '../../control/claim-creator-service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CondominiumListService} from '../../control/condominium-list-service';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Condominium} from '../../entity/condominium';
import {Observable, Subject} from 'rxjs';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {ObjectType} from '../../entity/claim-item';


@Component({
  selector: 'app-condominium-claim-metadata',
  imports: [
    CommonModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatOption,
    MatProgressSpinner,
    MatAutocompleteTrigger,
    MatAutocomplete,
    LocalizationPipePipe
  ],
  templateUrl: './condominium-claim-metadata.html',
  styleUrl: './condominium-claim-metadata.scss'
})
export class CondominiumClaimMetadata {
  @Output() afterClaimCreation = new EventEmitter<ClaimMetadataRequest>();
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Input() claimId?: string;
  isSubmitting: boolean = false;
  readonly metadataForm: FormGroup;
  protected readonly objectType: ObjectType = ObjectType.CondominiumUnit;
  protected condominiumOptions = ['Other'];
  protected condominiumFilteredOptions = [...(this.condominiumOptions)];
  private readonly selectedCondominiumSubject: Subject<Condominium | undefined> = new Subject<Condominium | undefined>();
  private selectedCondominium$: Observable<Condominium | undefined> = this.selectedCondominiumSubject.asObservable();
  protected error?: string;

  constructor(
    private fb: FormBuilder,
    private readonly claimService: ClaimCreatorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly condominiumListService: CondominiumListService,
    authService: AuthService,
    route: ActivatedRoute,
  ) {
    this.condominiumListService
      .getCondominiumListFor(
        authService.getCurrentUser()?.profile?.['custom:adjuster_company_id'] as string,
        authService.getCurrentUser()?.profile?.['custom:insurance_company_id'] as string
      )
      .then(r => {
        this.condominiumOptions = [...(r?.map(c => c.condominium))]
        this.condominiumFilteredOptions = [...(this.condominiumOptions)]
      })
    this.metadataForm = this.fb.group({
      adjusterID: [authService.getCurrentUser()?.profile?.sub],
      adjusterCompany: [authService.getCurrentUser()?.profile?.['custom:adjuster_company_id']],
      insuranceClaimReference: ['', [Validators.required]],
      lossAdjusterClaimReference: ['', [Validators.required]],
      condominiumName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      unitOwnerName: ['', [Validators.required]],
      unitNumber: ['', [Validators.required]]
    });

    this.metadataForm.valueChanges.subscribe(value => {
      this.error = undefined;
    })

    this.selectedCondominium$.subscribe((condominium: Condominium | undefined) => {
      if (!condominium) return;
      console.log('selectedCondominium', condominium)
      this.metadataForm.controls['insuranceClaimReference'].setValue(condominium.insuranceClaimReference?.toString(), {emitEvent: false});
      this.metadataForm.controls['lossAdjusterClaimReference'].setValue(condominium.lossAdjusterClaimReference?.toString(), {emitEvent: false});
      this.metadataForm.controls['address'].setValue(condominium.address?.toString(), {emitEvent: false});
    })

    this.metadataForm.controls['condominiumName'].valueChanges.subscribe(value => {
      console.log('value', value)
      const name = value ?? '';
      this.condominiumFilteredOptions = name ? this._filter(name as string) : this.condominiumOptions.slice();
      const selectedCondominium = this.condominiumListService.getCondominiumByName(value as string);
      this.selectedCondominiumSubject.next(selectedCondominium)
    })

    route.queryParams.subscribe(params => {
      if (params['insuranceClaimReference']) {
        this.metadataForm.controls['insuranceClaimReference'].setValue(params['insuranceClaimReference'], {emitEvent: false})
      }
      if (params['condominiumName']) {
        this.metadataForm.controls['condominiumName'].setValue(params['condominiumName'], {emitEvent: false})
      }
      if (params['lossAdjusterClaimReference']) {
        this.metadataForm.controls['lossAdjusterClaimReference'].setValue(params['lossAdjusterClaimReference'], {emitEvent: false})
      }
      if (params['address']) {
        this.metadataForm.controls['address'].setValue(params['address'], {emitEvent: false})
      }
    })

    // Subscribe to form validity changes
    this.metadataForm.statusChanges.subscribe(() => {
      this.formValidityChange.emit(this.metadataForm.valid);
    });

    // Emit initial validity state
    setTimeout(() => {
      this.formValidityChange.emit(this.metadataForm.valid);
    });
  }

  onInitiateClaim() {
    if (this.metadataForm.valid) {
      const formValues = this.metadataForm.value;
      const formData: ClaimMetadataRequest = {
        ...formValues,
        insuranceClaimReference: formValues.insuranceClaimReference.toString()
      };
      this.createClaimDraft(formData);
    } else {
      Object.keys(this.metadataForm.controls).forEach(key => {
        const control = this.metadataForm.get(key);
        if (control && control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  createClaimDraft(metadataForm: ClaimMetadataRequest) {
    if (this.claimId !== undefined) {
      metadataForm = {...metadataForm, objectType: this.objectType, claimID: this.claimId}
    }
    this.error = undefined;
    this.isSubmitting = true;
    this.cdr.detectChanges();
    const request: ClaimMetadataRequest = {} as ClaimMetadataRequest;
    for (const key in metadataForm) {
      request[key as keyof ClaimMetadataRequest] = (metadataForm[key as keyof ClaimMetadataRequest] ?? '').toString();
      console.log(`request[${key} as keyof ClaimMetadataRequest] = (${metadataForm[key as keyof ClaimMetadataRequest]} ?? '')`)
    }
    this.claimService.createClaimDraft(request)
      .then(r => {
        console.log('createClaimDraft done', request)
        this.afterClaimCreation.emit({...r});
      })
      .catch(error => {
        this.isSubmitting = false;
        this.error = error.message;
      })
      .finally(() => {
        this.isSubmitting = false;
        this.cdr.detectChanges();
      })
  }

  displayFn(condominium: string): string {
    return condominium ?? '';
  }

  private _filter(name: string): string[] {
    const filterValue = name.toLowerCase();
    return this.condominiumOptions.filter(option => option.toLowerCase().includes(filterValue));
  }

}
